import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { accountStatus, DOMAIN } from "../utils/constants";
import Viewer from "../components/Viewer";
import ActivityFeed from "../components/ActivityFeed";
import { clearLots, handleSelection } from "../redux/slice/liveAuctionSlice";
import { fetchLots } from "../redux/action/liveAuctionAction";
import useTranslationHook from "../components/hooks/useTranslationHook";
import {
    createSlug,
    formatNumber,
    formatPrice,
    getLotStatus,
    getPricePercentage,
    getTimeAgo,
    toEasternArabicNumerals,
} from "../helpers";
import { Helmet } from "react-helmet";
import Layout from "../components/common/layout/Index";
import CustomButton from "../components/partials/property-details/right-panel/CustomButton";
import FairWarning from "../components/FairWarning";
import SocketService from "../Service/SocketService";
import { fetchPropertyBidsHistory } from "../redux/action/bidHistoryAction";
import VerifyAccountModal from "../components/partials/property-details/right-panel/modals/VerifyAccountModal";
import PlaceBidModal from "../components/partials/property-details/right-panel/modals/PlaceBidModal";
import { setModalStep, setShowPlaceBidModal } from "../redux/slice/sellerSlice";
import moment from "moment";
import { handleSiteLoader } from "../redux/action/authAction";
import { fetchPropertyDetail } from "../redux/action/sellerAction";
import { handleViewProperty } from "../redux/action/profileAction";
import { toast } from "react-toastify";

export default function LiveAuctionUI() {
    const feedRef = useRef(null);
    const [collapsed, setCollapsed] = useState(false);
    const [showHeader, setShowHeader] = useState(true);
    const selectedLot = useSelector((state) => state.liveAuction.selectedLot);
    const [showEnded, setShowEnded] = useState(false);
    const [status, setStatus] = useState({ label: "", className: "" });

    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { selectedSessionID, lotID, propertyId } = location?.state || {};

    const user = useSelector((state) => state.auth.user);
    const userId = Number(user?.user_id) || user?.id;
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

    const { t } = useTranslationHook();
    let lang = useSelector((state) => state.translation.lang);

    const [accountVerifiedModal, setAccountVerifiedModal] = useState(false);
    const { showPlaceBidModal } = useSelector((state) => state.seller.property);

    // ─── POINT 9: PaymentFailureModal state ─────────────────────────────────────
    const [showFailureModal, setShowFailureModal] = useState(false);

    // ─── POINT 3 / POINT 2: showPopUp state ─────────────────────────────────────
    const [showPopUp, setShowPopUp] = useState(true);

    const prevTopBidRef = useRef(null);

    const [animateTopBid, setAnimateTopBid] = useState(false);

    const handlePlaceBidClick = (e) => {
        e?.stopPropagation?.();
        dispatch(setShowPlaceBidModal(true));
        dispatch(setModalStep(1));
    };

    const lots = useSelector((state) => state.liveAuction.lots);
    const { isConnected } = useSelector((state) => state.socket);
    const [viewerCount, setViewerCount] = useState({
        active_bidder_count: 0,
        bidder_data: 0,
        watcher_count: 0,
    });

    const [bidAction, setBidAction] = useState(false);
    const [endedLots, setEndedLots] = useState([]);
    const [activeLots, setActiveLots] = useState([]);
    const [highestBid, setHighestBid] = useState(0);

    const lot_id = selectedLot?.id || lotID;
    const property_id = selectedLot?.property_id || propertyId;
    const is_registered = selectedLot?.is_registered;

    const [liveData, setLiveData] = useState(null);
    const [bidHistory, setBidHistory] = useState([]);
    const [userHighestBid, setUserHighestBid] = useState(0);

    const auction_id = selectedLot?.property_auction_data?.[0]?.id;
    const bid_increments =
        selectedLot?.property_auction_data?.[0]?.bid_increments;
    const start_price = selectedLot?.property_auction_data?.[0]?.start_price;

    const highBidAmt = liveData?.high_bid_amt;

    // ─── POINT 10: minBidAmount correct calculation (LiveFeedCard pattern) ───────
    let minBidAmount = !parseInt(highBidAmt)
        ? parseInt(start_price)
        : parseInt(highBidAmt ?? start_price) +
          parseInt(liveData?.bid_increments || bid_increments);

    // ─── POINT 5: isRegistrationAllowed date check ───────────────────────────────
    const registration_open_date = selectedLot?.registration_open_date;
    const start_date = selectedLot?.property_auction_data?.[0]?.start_date;
    const end_date = selectedLot?.property_auction_data?.[0]?.end_date;
    const isAuctionStarted =
        new Date() >= new Date(selectedLot?.session_start_date);
    // const isRegistrationAllowed =
    //     (registration_open_date && moment().isSameOrAfter(registration_open_date)) ||
    //     (!registration_open_date && moment().isBetween(start_date, end_date, null, "[]"));

    // ─── POINT 7: showFinalHighestBidderMessage ──────────────────────────────────
    const reserveAmount = liveData?.reserve_amount;
    const isReserveMet = reserveAmount <= Number(highBidAmt);
    const isLotEnded = [8, 9, 16, 35].includes(
        selectedLot?.lot_status?.status_id
    );
    const isWinner = isLotEnded && selectedLot?.winner_id === userId;
    const isLeading = bidHistory?.[0]?.user === userId;
    const hasUserParticipated = userHighestBid > 0;

    const showFinalHighestBidderMessage =
        userId === liveData?.max_bidder_user_id &&
        (isReserveMet || liveData?.is_selected_highest_bid) &&
        isLotEnded;

    const syncEmitData = useMemo(
        () => ({
            property_id,
            user_id: userId || "",
            auction_id,
            domain_id: DOMAIN,
        }),
        [property_id, userId, auction_id]
    );

    const getUserMaxBid = useCallback((bids, userId) => {
        if (!bids?.length) return 0;

        let max = 0;
        const uid = Number(userId);

        for (let i = 0; i < bids.length; i++) {
            const bid = bids[i];
            if (!bid) continue;

            if (Number(bid.user) === uid) {
                const value = Number(bid.start_bid);
                if (value > max) max = value;
            }
        }

        return max;
    }, []);

    // let allowBidding = (Number(selectedLot?.available_bid_limit ?? 0)
    //     - Number(userHighestBid)) >= Number(minBidAmount)

    let allowBidding =
        Number(selectedLot?.available_bid_limit ?? 0) >= Number(minBidAmount);

    useEffect(() => {
        return () => {
            dispatch(clearLots());
        };
    }, []);

    useEffect(() => {
        setBidAction(!!selectedLot?.lot_pause_resume);
    }, [selectedLot?.lot_pause_resume]);

    // useEffect(() => {
    //     if (!propertyId || isNaN(Number(propertyId))) {
    //         toast.error(t("Invalid property id"));
    //         return navigate("/not-found");
    //     }
    //     dispatch(fetchPropertyDetail(propertyId));
    //     if (isAuthenticated) {
    //         dispatch(
    //             handleViewProperty({
    //                 ...(user?.site_id && {
    //                     site_id: user.site_id,
    //                 }),
    //                 property_id: propertyId,
    //             })
    //         );
    //     }
    //     //eslint-disable-next-line
    // }, [propertyId]);

    useEffect(() => {
        if (!userId || (!selectedSessionID && !propertyId)) {
            navigate(`/discover?view=grid`);
            return;
        }
        if (!selectedSessionID && propertyId) {
            navigate(`/property/detail/${propertyId}`);
            return;
        }
    }, []);

    // ─── POINT 9: PaymentFailureModal trigger ────────────────────────────────────
    useEffect(() => {
        setShowFailureModal(!!selectedLot?.payment_failed_status);
    }, [selectedLot?.payment_failed_status]);

    // ─── POINT 4: handleSiteLoader on disconnect ─────────────────────────────────
    useEffect(() => {
        if (!isConnected) {
            dispatch(handleSiteLoader(true));
        } else {
            dispatch(handleSiteLoader(false));
        }
    }, [isConnected]);

    useEffect(() => {
        if (!bidHistory?.length) return;
        const currentTop = bidHistory[0]?.id;
        if (prevTopBidRef.current && prevTopBidRef.current !== currentTop) {
            setAnimateTopBid(true);
            setTimeout(() => setAnimateTopBid(false), 600);
        }
        prevTopBidRef.current = currentTop;
    }, [bidHistory]);

    // ─── POINT 1: joinPropertyRoom auth-aware + isCallCheckBid ──────────────────
    useEffect(() => {
        if (!isConnected || !syncEmitData?.property_id) return;

        !isAuthenticated
            ? SocketService.send("joinPropertyRoom", {
                  property_id: syncEmitData.property_id,
              })
            : SocketService.send("joinPropertyRoom", {
                  property_id: syncEmitData.property_id,
                  user_id: userId || "",
              });

        SocketService.send("isCallCheckBid", {
            property_id: syncEmitData.property_id,
        });

        const handleIsCallCheckBid = ({ error }) => {
            if (error) return;
            SocketService.send("checkBid", syncEmitData);
        };

        SocketService.on("isCallCheckBid", handleIsCallCheckBid);

        return () => {
            SocketService.off("isCallCheckBid", handleIsCallCheckBid);
        };
    }, [isConnected, syncEmitData, userId, isAuthenticated]);

    useEffect(() => {
        if (!isConnected || !property_id) return;

        SocketService.send("checkBid", syncEmitData);

        const handleCheckBid = ({ error, data }) => {
            if (error || !data) return;
            if (data.property_id !== property_id) return;

            const bidPlaced = sessionStorage.getItem("bidPlaced");

            // ─── POINT 2: showPopUp guard ────────────────────────────────────────
            setShowPopUp(
                !data?.my_max_bid_val ||
                    (data?.is_last_bid_retracted &&
                        selectedLot?.is_approved === 2)
            );

            setLiveData((prev) => {
                const prevHigh = Number(prev?.high_bid_amt || 0);
                const newHigh = Number(data?.high_bid_amt || 0);

                if (bidPlaced && newHigh > prevHigh) {
                    sessionStorage.removeItem("bidPlaced");
                    dispatch(setShowPlaceBidModal(true));
                    dispatch(setModalStep(4));
                }

                return data;
            });
        };

        const handleResync = ({ data }) => {
            if (!data) return;
            SocketService.send("checkBid", { ...syncEmitData, ...data });
        };

        SocketService.on("checkBid", handleCheckBid);
        SocketService.on("re-sync", handleResync);

        return () => {
            SocketService.off("checkBid", handleCheckBid);
            SocketService.off("re-sync", handleResync);
        };
    }, [isConnected, property_id, syncEmitData]);

    useEffect(() => {
        if (!property_id) return;
        dispatch(fetchPropertyBidsHistory(property_id, "", "", "", true)).then(
            (res) => {
                if (res?.error) return;
                setViewerCount((prev) => ({
                    ...prev,
                    bidder_data: res?.data?.new_data?.length ?? 0,
                }));
                setBidHistory(res?.data?.new_data?.slice(0, 10) || []);
                setUserHighestBid(
                    getUserMaxBid(res?.data?.new_data ?? [], userId)
                );
            }
        );
    }, [property_id, selectedLot?.id]);

    useEffect(() => {
        if (!bidHistory || !highBidAmt) return;
        const latestBid = bidHistory[0]?.start_bid;
        if (Number(highBidAmt) !== Number(latestBid)) {
            dispatch(
                fetchPropertyBidsHistory(property_id, "", "", "", true)
            ).then((res) => {
                if (res?.error) return;
                setViewerCount((prev) => ({
                    ...prev,
                    bidder_data: res?.data?.new_data?.length ?? 0,
                }));
                setBidHistory(res?.data?.new_data?.slice(0, 10) || []);
                setUserHighestBid(
                    getUserMaxBid(res?.data?.new_data ?? [], userId)
                );
            });
        }
    }, [highBidAmt, property_id, dispatch]);

    useEffect(() => {
        const style = document.createElement("style");
        style.innerHTML = ``;
        document.head.appendChild(style);

        if (selectedSessionID) {
            dispatch(fetchLots({ session_id: selectedSessionID }));
        }
    }, []);

    useEffect(() => {
        if (!isConnected || !lot_id || !property_id || !userId) return;

        const payload = {
            session_lot_id: lot_id,
            user_id: userId,
            property_id: property_id,
            is_bidder: is_registered && selectedLot?.is_payment_completed,
        };

        SocketService.send("joinAuctionRoom", payload);

        const handleActiveBidderAdded = (data) => {
            setViewerCount((prev) => ({
                ...prev,
                active_bidder_count: data?.active_bidder_count || 0,
                watcher_count: data?.watcher_count || 0,
            }));
        };

        const handleWatcherRemoved = (data) => {
            setViewerCount((prev) => ({
                ...prev,
                watcher_count: data?.watcher_count || 0,
            }));
        };

        const handlePauseResumeSessionLot = (data) => {
            const {
                action,
                status: statusCode,
                error,
                message,
                property_id,
                session_id,
                lot_id,
            } = data || {};

            if (data?.action_type?.toLowerCase() === "start") {
                dispatch(fetchLots({ session_id: selectedSessionID }));
            }

            // toast.info(status?.label?.toLowerCase() === "upcoming" ? "The auction has started" : message)
            toast.info(
                data?.action_type?.toLowerCase() === "start"
                    ? "The auction has started"
                    : message
            );
            if (statusCode === 200 && error === 0) {
                setBidAction(Boolean(action));
            }
        };

        const handleHighlightHighestBid = (data) => {
            const { highest_bid, status, error } = data || {};
            if (status === 200 && error === 0) {
                setHighestBid(highest_bid || 0);
            }
        };

        const handleAuctionLotEnded = (data) => {
            const {
                status,
                error,
                message,
                winning_bid_amount,
                reserve_price,
                sale_status,
                property_status,
            } = data || {};
            toast.info(message);
            if (status === 200 && error === 0) {
                dispatch(fetchLots({ session_id: selectedSessionID }));
                // const bid = Number(winning_bid_amount);
                // if (!bid) return 9;
                // if (bid >= reserve_price) return 8;
                // return 16;
                if (!selectedLot?.id) return;
                setEndedLots((prev) => [
                    {
                        ...selectedLot,
                        lot_status: {
                            ...selectedLot?.lot_status,
                            status_id: sale_status,
                        },
                    },
                    ...prev,
                ]);
                setActiveLots((prev) =>
                    selectedLot?.id
                        ? prev.filter((lot) => lot.id !== selectedLot.id)
                        : prev
                );
            }
        };

        const handleBidIncrementUpdate = (data) => {
            const { bid_increment_val, status, error } = data || {};
            if (status === 200 && error === 0) {
                setLiveData((prev) => ({
                    ...prev,
                    bid_increments: bid_increment_val,
                }));
            }
        };

        SocketService.on("activeBidderAdded", handleActiveBidderAdded);
        SocketService.on("watcherRemoved", handleWatcherRemoved);
        SocketService.on("pauseResumeSessionLot", handlePauseResumeSessionLot);
        SocketService.on("highlightHighestBid", handleHighlightHighestBid);
        SocketService.on("auctionLotEnded", handleAuctionLotEnded);
        SocketService.on("bidIncrementUpdate", handleBidIncrementUpdate);

        return () => {
            SocketService.off("activeBidderAdded", handleActiveBidderAdded);
            SocketService.off("watcherRemoved", handleWatcherRemoved);
            SocketService.send("leaveAuctionRoom", `auction_${lot_id}`);
            SocketService.off(
                "pauseResumeSessionLot",
                handlePauseResumeSessionLot
            );
            SocketService.off("highlightHighestBid", handleHighlightHighestBid);
            SocketService.off("auctionLotEnded", handleAuctionLotEnded);
            SocketService.off("bidIncrementUpdate", handleBidIncrementUpdate);
        };
    }, [isConnected, lot_id, property_id, userId]);

    // useEffect(() => {
    //     const nextLot = activeLots?.[0] || endedLots?.[0] || null;
    //     if (nextLot) {
    //         dispatch(handleSelection({ selectedLot: nextLot }));
    //     }
    // }, [activeLots, endedLots]);

    // useEffect(() => {
    //     const selectedLot =
    //         lots?.data.find(
    //             (item) =>
    //                 item?.session_status?.status_id === 1 &&
    //                 item?.lot_status?.status_id === 1 &&
    //                 item?.is_initiated
    //         ) ||
    //         lots?.data.find((item) => lots?.data.find((item) => item.id === lotID)) ||
    //         null;

    //     dispatch(handleSelection({ selectedLot }));

    // }, [lots?.data, lotID]);

    // useEffect(() => {
    //     if (!lots?.data?.length) return;

    //     // 1. Try to keep current selected lot (VERY IMPORTANT)
    //     let currentSelected = lots.data.find(item => item.id === selectedLot?.id);

    //     if (currentSelected) {
    //         dispatch(handleSelection({ selectedLot: currentSelected }));
    //         return;
    //     }

    //     // 2. Fallback: try lotID from route
    //     let routeLot = lots.data.find(item => item.id === lotID);

    //     if (routeLot) {
    //         dispatch(handleSelection({ selectedLot: routeLot }));
    //         return;
    //     }

    //     // 3. Fallback: active lot
    //     let activeLot = lots.data.find(
    //         (item) =>
    //             item?.session_status?.status_id === 1 &&
    //             item?.lot_status?.status_id === 1 &&
    //             item?.is_initiated
    //     );

    //     if (activeLot) {
    //         dispatch(handleSelection({ selectedLot: activeLot }));
    //         return;
    //     }

    //     // 4. FINAL fallback: keep first safely (optional)
    //     dispatch(handleSelection({ selectedLot: lots.data[0] }));

    // }, [lots?.data]);

    useEffect(() => {
        if (!lots?.data?.length) return;

        // 1. FIRST PRIORITY: route lot (VERY IMPORTANT)
        let routeLot = lots.data.find((item) => item.id === lotID);

        if (routeLot) {
            dispatch(handleSelection({ selectedLot: routeLot }));
            return;
        }

        // 2. Keep current selected (only if no route lot)
        let currentSelected = lots.data.find(
            (item) => item.id === selectedLot?.id
        );

        if (currentSelected) {
            dispatch(handleSelection({ selectedLot: currentSelected }));
            return;
        }

        // 3. Active lot fallback
        let activeLot = lots.data.find(
            (item) =>
                item?.session_status?.status_id === 1 &&
                item?.lot_status?.status_id === 1 &&
                item?.is_initiated
        );

        if (activeLot) {
            dispatch(handleSelection({ selectedLot: activeLot }));
            return;
        }

        // 4. Final fallback
        dispatch(handleSelection({ selectedLot: lots.data[0] }));
    }, [lots?.data, lotID]);

    useEffect(() => {
        setStatus(
            getLotStatus(
                selectedLot?.session_status?.status_id,
                selectedLot?.lot_status?.status_id,
                selectedLot?.is_initiated,
                true
            )
        );
    }, [
        selectedLot?.session_status?.status_id,
        selectedLot?.lot_status?.status_id,
        selectedLot?.is_initiated,
    ]);

    // useEffect(() => {
    //     setStatus(
    //         getLotStatus(
    //             selectedLot?.session_status?.status_id,
    //             selectedLot?.lot_status?.status_id,
    //             selectedLot?.is_initiated,
    //             true
    //         )
    //     );
    // }, [selectedLot?.lot_status?.status_id],);

    // ─── POINT 8: navigate('/close-live-outbid') on auction end ─────────────────
    // useEffect(() => {
    //     const lotStatusId = selectedLot?.lot_status?.status_id;

    //     if (
    //         liveData?.my_max_bid_val &&
    //         liveData?.max_bidder_user_id !== userId &&
    //         prevLotStatusRef.current === 1 &&
    //         (lotStatusId === 8 || lotStatusId === 9)
    //     ) {
    //         navigate("/close-live-outbid");
    //     }

    //     prevLotStatusRef.current = lotStatusId;
    // }, [selectedLot?.lot_status?.status_id]);

    const increment = 50000;

    const onClickBidRegistration = () => {
        if (!userId) {
            navigate(
                `/sign-in?redirect=${encodeURIComponent(location.pathname)}`
            );
            return;
        } else {
            if (
                (user?.user_account_verification ||
                    user?.is_account_verified) !== accountStatus?.success
            ) {
                setAccountVerifiedModal(true);
                return;
            }
        }

        navigate("/bidder-registration", {
            state: {
                propertyId: selectedLot?.property_id,
                depositAmount: selectedLot?.deposit_amount,
                propertyName: selectedLot?.property_name,
                propertyNameAR: selectedLot?.property_name_ar,
                currentPathName: window.location.href,
                propertyAuctionDate: selectedLot?.session_start_date,
                propertyState: selectedLot?.state,
                propertyCommunity: selectedLot?.community,
                propertyCommunityAr: selectedLot?.community_ar,
                selectedSessionID: selectedSessionID,
                lotID: selectedLot?.id,
                selfRegistration: true,
            },
        });
    };

    const onClickIncreaseBidLimit = () => {
        if (!userId) {
            navigate(
                `/sign-in?redirect=${encodeURIComponent(location.pathname)}`
            );
            return;
        }
        navigate("/bidder-registration", {
            state: {
                propertyId: selectedLot?.property_id,
                depositAmount: selectedLot?.deposit_amount,
                propertyName: selectedLot?.property_name,
                propertyNameAR: selectedLot?.property_name_ar,
                currentPathName: window.location.href,
                propertyAuctionDate: selectedLot?.session_start_date,
                propertyState: selectedLot?.state,
                propertyCommunity: selectedLot?.community,
                propertyCommunityAr: selectedLot?.community_ar,
                selectedSessionID: selectedSessionID,
                lotID: selectedLot?.id,
                isBidLimitIncrease: true,
            },
        });
    };

    // ─── POINT 3: isRetractedBid guard in handlePlaceBidClick ───────────────────
    const handlePlaceBidClickGuarded = (e) => {
        e?.stopPropagation?.();
        if (!allowBidding) {
            toast.info(t("The next bid exceeds your available limit."));
            return;
        }

        if (!isAuctionStarted) {
            toast.info(t("Bidding has not started yet"));
            return;
        }

        if (sessionStorage.getItem("isRetractedBid")) {
            sessionStorage.removeItem("isRetractedBid");
            return;
        }

        dispatch(setShowPlaceBidModal(true));
        dispatch(setModalStep(1));
    };

    useEffect(() => {
        const endedLots = (lots?.data || []).filter(
            (lot) =>
                lot?.lot_status?.status_id === 8 ||
                lot?.lot_status?.status_id === 9 ||
                lot?.lot_status?.status_id === 16 ||
                lot?.lot_status?.status_id === 35
        );

        const activeLots = (lots?.data || []).filter(
            (lot) =>
                (lot?.session_status?.status_id === 1 &&
                    lot?.lot_status?.status_id === 1 &&
                    lot?.is_initiated) ||
                lot?.session_status?.status_id === 37 ||
                (lot?.session_status?.status_id === 1 &&
                    lot?.lot_status?.status_id === 1 &&
                    !lot?.is_initiated) ||
                lot?.session_status?.status_id === 41
        );
        setEndedLots(endedLots);
        setActiveLots(activeLots);
    }, [lots?.data]);

    const isOutbid =
        bidHistory?.length > 0 &&
        bidHistory?.[0]?.user !== userId &&
        bidHistory?.some((b) => b.user === userId);

    return (
        <>
            <Helmet>
                <title>{t("Live Auction")}</title>
            </Helmet>

            <div className={`${showHeader ? "s-how" : "h-ide"} live-header`}>
                <Link to={"/"} className="logo">
                    <img src="/img/logo.svg" alt="" />
                    <span>{t("Live Auction")}</span>
                </Link>
                <div className="bid-limit">
                    <h6>
                        {t("Bidding limit")}:{" "}
                        <span>
                            <em className="dirham">D</em>{" "}
                            {formatPrice(userHighestBid, lang, "", false)}
                        </span>{" "}
                        / <em className="dirham">D</em>
                        {formatPrice(
                            selectedLot?.available_bid_limit,
                            lang,
                            "",
                            false
                        )}
                    </h6>
                    {selectedLot?.is_registered &&
                        selectedLot?.is_payment_completed && (
                            <button
                                className="btn btn-xs btn-primary ml-2"
                                onClick={onClickIncreaseBidLimit}
                            >
                                {t("Increase Limit")}
                            </button>
                        )}
                </div>

                <div className="live-right">
                    <div className="dropdown">
                        <button className="noti-link">
                            <i className="fa-solid fa-bell"></i>
                        </button>
                        <FairWarning />
                    </div>
                    <button
                        className="close-header"
                        onClick={
                            () =>
                                navigate(
                                    `/property/detail/${selectedLot?.property_id}`
                                )
                            // setShowHeader((prev) => !prev)
                        }
                    >
                        <img src="/img/close-small.svg" alt="close" />
                    </button>
                </div>
            </div>

            <div
                className={`${!showHeader ? "livewrapincrease" : ""} live-wrap`}
            >
                {/* ================= SIDEBAR ================= */}
                <div className={`left-sidebar ${collapsed ? "reduceW" : ""}`}>
                    <div className="sidebarheader">
                        {!collapsed && (
                            <h6>
                                {t("Auction Catalogue")}
                                <span>
                                    {t(
                                        activeLots.length === 0 ||
                                            activeLots.length === 1
                                            ? "Auction Count"
                                            : "Auctions Count",
                                        { count: activeLots.length }
                                    )}{" "}
                                    ({t("Session ID")}: {selectedSessionID} )
                                </span>
                            </h6>
                        )}
                        <button
                            className="collapsed-btn"
                            onClick={() => setCollapsed(!collapsed)}
                        >
                            <img src="img/toggle-icon.svg" alt="Toggle" />
                        </button>
                    </div>

                    {!collapsed && (
                        <>
                            <div className="auction-list scrollable">
                                {activeLots.map((lot) => {
                                    const isSelected =
                                        lot.id === selectedLot?.id;
                                    const status = getLotStatus(
                                        lot?.session_status?.status_id,
                                        lot?.lot_status?.status_id,
                                        lot?.is_initiated,
                                        true
                                    );
                                    return (
                                        <div
                                            className={`auction-box ${isSelected ? "active" : ""}`}
                                            key={lot.id}
                                            onClick={() =>
                                                dispatch(
                                                    handleSelection({
                                                        selectedLot: lot,
                                                    })
                                                )
                                            }
                                        >
                                            <h6>
                                                {
                                                    lot
                                                        .property_auction_data?.[0]
                                                        ?.auction_unique_id
                                                }
                                                :{" "}
                                                {lang === "en"
                                                    ? lot.property_name
                                                    : lot?.property_name_ar}
                                            </h6>
                                            <div className="cardbottom">
                                                <span className="price">
                                                    {t("Starting Bid")} :{" "}
                                                    <em className="dirham">
                                                        D
                                                    </em>{" "}
                                                    {Number(
                                                        lot
                                                            ?.property_auction_data?.[0]
                                                            ?.start_price || 0
                                                    ).toLocaleString()}
                                                </span>
                                                <span
                                                    className={`badge ${lot?.lot_status?.status_name === "Bought In" ? "badge-warning" : status?.className}`}
                                                >
                                                    {t(
                                                        lot?.lot_status
                                                            ?.status_name ===
                                                            "Bought In"
                                                            ? lot?.lot_status
                                                                  ?.status_name
                                                            : status?.label
                                                    )}
                                                </span>
                                            </div>
                                            <span className="arrow">
                                                <img
                                                    src="/img/arrow-r.svg"
                                                    alt=""
                                                />
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mt10">
                                <div
                                    className="ended-auction"
                                    style={styles.endedHeader}
                                    onClick={() => setShowEnded(!showEnded)}
                                >
                                    <span>
                                        {t("Ended Auctions", {
                                            count: endedLots.length,
                                        })}
                                    </span>
                                    <span>
                                        {showEnded ? (
                                            <img src="/img/live-arrow-up.svg" />
                                        ) : (
                                            <img src="/img/live-arrow-down.svg" />
                                        )}
                                    </span>
                                </div>

                                {showEnded && (
                                    <div
                                        style={styles.endedList}
                                        className="auction-list scrollable"
                                    >
                                        {endedLots.map((lot) => {
                                            const isSelected =
                                                lot.id === selectedLot?.id;
                                            const status = getLotStatus(
                                                lot?.session_status?.status_id,
                                                lot?.lot_status?.status_id,
                                                lot?.is_initiated,
                                                true
                                            );
                                            return (
                                                <div
                                                    className={`auction-box ${isSelected ? "active" : ""}`}
                                                    key={lot.id}
                                                    onClick={() =>
                                                        dispatch(
                                                            handleSelection({
                                                                selectedLot:
                                                                    lot,
                                                            })
                                                        )
                                                    }
                                                >
                                                    <h6>
                                                        {
                                                            lot
                                                                .property_auction_data?.[0]
                                                                ?.auction_unique_id
                                                        }
                                                        :{" "}
                                                        {lang === "en"
                                                            ? lot.property_name
                                                            : lot?.property_name_ar}
                                                    </h6>
                                                    <div className="cardbottom">
                                                        <span className="price">
                                                            {t("Starting Bid")}{" "}
                                                            :{" "}
                                                            <em className="dirham">
                                                                D
                                                            </em>{" "}
                                                            {Number(
                                                                lot
                                                                    .property_auction_data?.[0]
                                                                    ?.start_price ||
                                                                    0
                                                            ).toLocaleString()}
                                                        </span>
                                                        <span
                                                            className={`badge ${lot?.lot_status?.status_name === "Bought In" ? "badge-warning" : status?.className}`}
                                                        >
                                                            {t(
                                                                lot?.lot_status
                                                                    ?.status_name ===
                                                                    "Bought In"
                                                                    ? lot
                                                                          ?.lot_status
                                                                          ?.status_name
                                                                    : status?.label
                                                            )}
                                                        </span>
                                                    </div>
                                                    <span className="arrow">
                                                        <img
                                                            src="/img/arrow-r.svg"
                                                            alt=""
                                                        />
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {collapsed && (
                        <div className="num-list">
                            {activeLots.map((lot) => {
                                const isSelected = lot.id === selectedLot?.id;
                                return (
                                    <div
                                        style={{ cursor: "pointer" }}
                                        className={`circle ${isSelected ? "active" : ""}`}
                                        key={lot.id}
                                        onClick={() =>
                                            dispatch(
                                                handleSelection({
                                                    selectedLot: lot,
                                                })
                                            )
                                        }
                                    >
                                        <span className="customTooltip">
                                            {
                                                lot.property_auction_data?.[0]
                                                    ?.auction_unique_id
                                            }
                                        </span>
                                        <img
                                            src="/img/lot-icon.svg"
                                            alt="lot Icon"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* ================= CENTER ================= */}
                <div className={`center-wrap ${collapsed ? "increaseW" : ""}`}>
                    {/* VIDEO */}
                    <Viewer
                        selectedSessionID={selectedSessionID}
                        viewerCount={viewerCount}
                    />

                    {/* SCROLLABLE ACTIVITY FEED */}

                    <ActivityFeed feedRef={feedRef} lot_id={lotID} />
                </div>

                {/* ================= RIGHT PANEL ================= */}
                <div className="rightpanel">
                    <div className="auction-top">
                        <div className="pic">
                            <img
                                src={`${process.env.REACT_APP_AZURE_BLOB_URL}${selectedLot?.cover_image?.[0]?.bucket_name}/${selectedLot?.cover_image?.[0]?.doc_file_name}`}
                            />
                            <span className="offplan">
                                {t(`${selectedLot?.construction_status_name}`)}
                            </span>
                        </div>

                        <div className="content">
                            <div className="types-top">
                                <h6>
                                    {
                                        selectedLot?.property_auction_data?.[0]
                                            ?.auction_unique_id
                                    }
                                    :{" "}
                                    {lang === "en"
                                        ? selectedLot?.property_name
                                        : selectedLot?.property_name_ar}
                                </h6>
                                <span
                                    className={`badge ${selectedLot?.lot_status?.status_name === "Bought In" ? "badge-warning" : status?.className}`}
                                >
                                    {t(
                                        selectedLot?.lot_status?.status_name ===
                                            "Bought In"
                                            ? selectedLot?.lot_status
                                                  ?.status_name
                                            : status?.label
                                    )}
                                </span>
                            </div>

                            <p className="location">
                                {lang === "en"
                                    ? selectedLot?.community
                                    : selectedLot?.community_ar}
                                , {selectedLot?.state}
                            </p>

                            <div className="types-box">
                                <ul className="types">
                                    <li>
                                        <img src="/img/bed-icon.svg" alt="" />{" "}
                                        {`${selectedLot?.beds}`.toLowerCase() !==
                                        "studio"
                                            ? toEasternArabicNumerals(
                                                  selectedLot?.beds
                                                      ?.slice(0, 2)
                                                      .trim(),
                                                  lang
                                              )
                                            : ""}
                                        <span>
                                            {`${selectedLot?.beds}`.toLowerCase() ===
                                            "studio"
                                                ? t("Studio")
                                                : t(
                                                      toEasternArabicNumerals(
                                                          selectedLot?.beds
                                                              ?.slice(0, 2)
                                                              .trim(),
                                                          lang
                                                      ) === "1"
                                                          ? "Bed"
                                                          : "Beds"
                                                  )}
                                        </span>
                                    </li>
                                    <li>
                                        <img src="/img/bath-icon.svg" alt="" />{" "}
                                        {selectedLot?.baths}
                                        <span>{t("Baths")}</span>
                                    </li>
                                    <li>
                                        <img src="/img/ruler-icon.svg" alt="" />{" "}
                                        {selectedLot?.square_footage || 0}
                                        <span>{t("Square Feet",{
                                                squareFeet: "",
                                            })}</span>
                                    </li>
                                </ul>
                                <span
                                    className="more-link"
                                    onClick={() =>
                                        navigate(
                                            `/property/detail/${selectedLot?.property_id}`
                                        )
                                    }
                                >
                                    {t("More details")}{" "}
                                    <img src="/img/arrow-right.svg" alt="" />
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="estimate-box">
                        <div
                            className={`estimate-row ${
                                status.label.toLowerCase() === "upcoming"
                                    ? "no-status"
                                    : ""
                            }`}
                        >
                            <h6>
                                <span>{t("Propify Estimate")}</span>
                                <em className="dirham">D</em>{" "}
                                {t("Amount", {
                                    amount: `${formatPrice(
                                        selectedLot?.estimated_price,
                                        lang,
                                        "",
                                        false
                                    )}`,
                                })}
                            </h6>
                            {status.label.toLowerCase() !== "upcoming" ? (
                                <span className="badge badge-success">
                                    {
                                        getPricePercentage(
                                            bidHistory?.[0]?.start_bid ??
                                                selectedLot
                                                    ?.property_auction_data?.[0]
                                                    ?.start_price,
                                            selectedLot?.estimated_price,
                                            t
                                        )?.label
                                    }
                                </span>
                            ) : null}
                        </div>
                    </div>
                    {isLotEnded &&
                    selectedLot?.winner_id &&
                    hasUserParticipated ? (
                        <div
                            className={`blank-lot ${selectedLot?.winner_id === userId ? "win" : ""}`}
                        >
                            {selectedLot?.winner_id !== userId
                                ? t("This lot went to another bidder!")
                                : isReserveMet
                                  ? t("Congratulations! You have won this lot.")
                                  : t(
                                        "You are the highest bidder for this lot."
                                    )}
                        </div>
                    ) : null}

                    {status.label.toLowerCase() === "upcoming" ? (
                        <div className="text-center">
                            <h6>
                                {t("Starting Bid")}:{" "}
                                {formatNumber(
                                    selectedLot?.property_auction_data?.[0]
                                        ?.start_price,
                                    lang
                                )}
                            </h6>
                        </div>
                    ) : null}

                    <ul
                        className={`bidList ${
                            status.label.toLowerCase() === "upcoming"
                                ? "center"
                                : ""
                        }`}
                    >
                        {status.label.toLowerCase() === "upcoming" ? (
                            <li className="no-data">
                                <div className="icon">
                                    <img src="/img/hummer-black.svg" alt="" />
                                </div>
                                <h6>
                                    {t("Bidding hasn't started yet")}
                                    <span>
                                        {t(
                                            "This lot is upcoming and will open for bids soon"
                                        )}
                                    </span>
                                </h6>
                            </li>
                        ) : null}

                        {status.label.toLowerCase() === "active" ||
                        status.label.toLowerCase() === "closed" ? (
                            Array.isArray(bidHistory) && !bidHistory.length ? (
                                <li>
                                    <h6
                                        className="text-center"
                                        style={{ margin: "auto" }}
                                    >
                                        {t("No bids were placed on this lot.")}
                                    </h6>
                                </li>
                            ) : (
                                bidHistory
                                    ?.filter((bid) => !bid?.is_retracted)
                                    ?.map((bid, i, arr) => {
                                        const isYou = bid?.user === userId;
                                        const isTop = i === 0;

                                        const previousBid = arr[i - 1];
                                        const wasYouTop =
                                            previousBid?.user === userId;

                                        const isActive = isTop && isYou;
                                        const isProgress = isYou && !isTop;
                                        const isOutbid =
                                            i === 0 && // only top item
                                            bid?.user !== userId && // top is not you
                                            arr[1]?.user === userId;
                                        const highlightBid =
                                            Number(highestBid) ===
                                            Number(bid?.start_bid || 0);

                                        return (
                                            <li
                                                key={i}
                                                className={
                                                    highlightBid
                                                        ? "highlight"
                                                        : isActive
                                                          ? "active"
                                                          : isOutbid
                                                            ? "outbid"
                                                            : isProgress
                                                              ? "progresss"
                                                              : isYou
                                                                ? "you"
                                                                : ""
                                                }
                                                style={{
                                                    ...(i !== 0
                                                        ? {
                                                              ...styles.bidItem,
                                                              opacity:
                                                                  1 - i * 0.15,
                                                          }
                                                        : {}),

                                                    transition:
                                                        "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",

                                                    transform:
                                                        isTop && animateTopBid
                                                            ? "scale(1.05)"
                                                            : "scale(1)",

                                                    animation: isOutbid
                                                        ? "outbidShake 0.4s ease"
                                                        : isTop && animateTopBid
                                                          ? "pulse 0.4s ease"
                                                          : "none",
                                                }}
                                            >
                                                <span className="num">
                                                    {isYou ? (
                                                        "You"
                                                    ) : (
                                                        <>
                                                            <span>#</span>
                                                            <span>
                                                                {arr.length - i}
                                                            </span>
                                                        </>
                                                    )}
                                                </span>

                                                <h2
                                                    className="price"
                                                    style={
                                                        i !== 0
                                                            ? {
                                                                  fontSize:
                                                                      30 -
                                                                      i * 1.2,
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    <em className="dirham">
                                                        D
                                                    </em>{" "}
                                                    {Number(
                                                        bid?.start_bid || 0
                                                    ).toLocaleString()}
                                                </h2>

                                                <span className="duration">
                                                    {getTimeAgo(
                                                        bid?.bid_time,
                                                        lang
                                                    )}
                                                </span>

                                                {isTop && animateTopBid && (
                                                    <span
                                                        style={{
                                                            position:
                                                                "absolute",
                                                            right: "10px",
                                                            top: "6px",
                                                            fontSize: "10px",
                                                            fontWeight: 600,
                                                            color: "#b45309",
                                                            background:
                                                                "#fde68a",
                                                            padding: "2px 6px",
                                                            borderRadius: "6px",
                                                        }}
                                                    >
                                                        {t("NEW")}
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    })
                            )
                        ) : null}
                    </ul>

                    {/* ─── POINT 7: showFinalHighestBidderMessage ─────────────────── */}
                    {/* {showFinalHighestBidderMessage && (
                        <div className="msg-status text-center">
                            <div className="msg success-msg">
                                {purchaseForefitStatus === 1
                                    ? t(
                                        "Sold! Your highest bid won the property. Our representative will contact you to proceed."
                                    )
                                    : t("You won the highest bid! 🥳")}
                            </div>
                        </div>
                    )} */}

                    {/* CTA */}
                    {/* ─── POINT 7: Start Purchase Process button ─────────────────── */}
                    {showFinalHighestBidderMessage && isReserveMet ? (
                        <div className="register-box place-bid">
                            <button
                                className="btn btn-primary btn-full"
                                disabled={
                                    selectedLot?.purchase_forefit_status ===
                                        1 ||
                                    selectedLot?.purchase_forefit_status === 2
                                        ? true
                                        : false
                                }
                                onClick={() =>
                                    navigate(
                                        `/auction-winner/${selectedLot?.property_id}`
                                    )
                                }
                            >
                                {t("Start Purchase Process")}
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* ─── POINT 5: isRegistrationAllowed wraps register box ── */}
                            {/* {
                            isRegistrationAllowed &&
                                selectedLot?.session_status?.status_id === 1 &&
                                !(selectedLot?.is_registered && selectedLot?.is_payment_completed) ? (
                                <div className="register-box">
                                    <div className="icon">
                                        <img src="/img/user-add.svg" alt="" />
                                    </div>
                                    <h6>{t("Register to Bid")}</h6>
                                    <CustomButton
                                        label={t("Register Now")}
                                        className="btn btn-primary btn-sm"
                                        onClick={onClickBidRegistration}
                                        disabled={
                                            selectedLot?.is_registered &&
                                            selectedLot?.is_payment_completed
                                        }
                                        isRemoveDefaultButtonClass
                                    />
                                </div>
                            ) : null} */}
                            {(selectedLot?.session_status?.status_id === 1 ||
                                selectedLot?.session_status?.status_id ===
                                    41) &&
                            !(
                                selectedLot?.is_registered &&
                                selectedLot?.is_payment_completed
                            ) &&
                            !(
                                selectedLot?.lot_status?.status_id === 8 ||
                                selectedLot?.lot_status?.status_id === 9 ||
                                selectedLot?.lot_status?.status_id === 16 ||
                                selectedLot?.lot_status?.status_id === 35
                            ) ? (
                                <div className="register-box">
                                    <div className="icon">
                                        <img src="/img/user-add.svg" alt="" />
                                    </div>
                                    <h6>{t("Register to Bid")}</h6>
                                    <CustomButton
                                        label={t("Register Now")}
                                        className="btn btn-primary btn-sm"
                                        onClick={onClickBidRegistration}
                                        disabled={
                                            selectedLot?.is_registered &&
                                            selectedLot?.is_payment_completed
                                        }
                                        isRemoveDefaultButtonClass
                                    />
                                </div>
                            ) : (
                                <></>
                            )}

                            {selectedLot?.session_status?.status_id === 1 &&
                            selectedLot?.lot_status?.status_id === 1 &&
                            selectedLot?.is_initiated &&
                            selectedLot?.is_registered &&
                            selectedLot?.is_payment_completed ? (
                                <div className="register-box place-bid">
                                    <div className="leading">
                                        {isLeading ? (
                                            <span className="lead success">
                                                <img
                                                    src="/img/dou-arrow.svg"
                                                    alt=""
                                                    className="mr4"
                                                />
                                                {t("You're Leading")}
                                            </span>
                                        ) : isOutbid ? (
                                            <span className="lead danger">
                                                {t("Out of the Lead")}
                                            </span>
                                        ) : null}
                                    </div>
                                    {/* ─── POINT 3: guarded handlePlaceBidClick ───────── */}
                                    <button
                                        className="btn btn-primary btn-full"
                                        onClick={handlePlaceBidClickGuarded}
                                        disabled={!bidAction}
                                    >
                                        <img
                                            src="/img/hummer-icon.svg"
                                            alt=""
                                        />{" "}
                                        {t("Place Bid")}
                                        {/* (+
                                        {increment.toLocaleString()}) */}
                                    </button>
                                    <div className="price-increment">
                                        <div className="block">
                                            {t("Increment")}:{" "}
                                            <strong>
                                                {formatNumber(
                                                    Number(
                                                        liveData?.bid_increments ||
                                                            bid_increments
                                                    ),
                                                    lang
                                                )}
                                            </strong>
                                        </div>
                                        <div className="block">
                                            {t("Next bid")}:{" "}
                                            <strong>
                                                {formatNumber(
                                                    Number(minBidAmount),
                                                    lang
                                                )}
                                            </strong>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            </div>

            <VerifyAccountModal
                show={accountVerifiedModal}
                onClose={() => setAccountVerifiedModal(false)}
            />

            {isConnected && liveData && (
                <PlaceBidModal
                    show={showPlaceBidModal}
                    onClose={() => dispatch(setShowPlaceBidModal(false))}
                    data={liveData}
                    user={user}
                    auction_id={auction_id}
                    hideDeposit={true}
                    isLiveLot={true}
                    liveRegistrationId={selectedLot?.registration_id}
                    allowBidding={allowBidding}
                    sessionID={selectedSessionID}
                />
            )}

            {/* ─── POINT 9: PaymentFailureModal ──────────────────────────────────── */}
            {showFailureModal && (
                <PaymentFailureModal
                    show={showFailureModal}
                    onClose={() => setShowFailureModal(false)}
                    transactionId={selectedLot?.reg_transaction_id}
                    failureMsg={selectedLot?.payment_failed_message}
                    paymentErrorText={selectedLot?.payment_error_text}
                />
            )}
        </>
    );
}

const styles = {
    topScroll: {
        marginTop: 10,
        overflowY: "auto",
        maxHeight: "50vh",
        paddingRight: 4,
    },

    video: {
        width: "100%",
        height: 420,
        borderRadius: 16,
        objectFit: "cover",
    },

    overlayVideo: {
        position: "absolute",
        bottom: 20,
        right: 20,
        width: 180,
    },

    overlayImg: { width: "100%", borderRadius: 10 },
};
